
import React, { useState } from 'react';
import { ShieldCheck, CheckCircle2 } from 'lucide-react';
import { TERMS_DOCUMENT } from '../content/legalDocuments';

interface TermsOfUseProps {
  onAccept: () => void;
}

const TermsOfUse: React.FC<TermsOfUseProps> = ({ onAccept }) => {
  const [agreed, setAgreed] = useState(false);

  return (
    <div className="fixed inset-0 bg-slate-950 z-[100] flex items-center justify-center p-4 md:p-8 overflow-hidden font-sans">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_50%_50%,rgba(79,70,229,0.1),transparent_70%)]"></div>
      </div>

      <div className="bg-white dark:bg-slate-900 w-full max-w-4xl max-h-[90vh] rounded-[2rem] sm:rounded-[3rem] shadow-2xl border dark:border-slate-800 flex flex-col relative overflow-hidden animate-in zoom-in-95 duration-500">
        <div className="p-5 sm:p-8 border-b dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50 flex items-center gap-4">
          <div className="p-3 bg-indigo-600 rounded-2xl text-white">
            <ShieldCheck size={28} />
          </div>
          <div>
            <h2 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">Termos e Condições de Uso e Política de Privacidade</h2>
            <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mt-1">Por favor, leia atentamente para continuar utilizando o Allodos</p>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5 sm:p-8 md:p-12 space-y-6 text-slate-700 dark:text-slate-300 custom-scrollbar bg-white dark:bg-slate-900">
          <div className="prose prose-slate dark:prose-invert max-w-none">
            <p className="font-medium text-slate-600 dark:text-slate-400">
              Para continuar utilizando o Allodos, por favor leia atentamente e aceite os termos abaixo.
            </p>

            {/* Redação integral fornecida pelo Product Owner (bloco 30):
                content/legalDocuments.ts é a única fonte, compartilhada com o
                Repositório de Documentos. */}
            <h3 className="font-black text-xl text-indigo-600 dark:text-indigo-400 mt-8">{TERMS_DOCUMENT.title}</h3>
            <p className="text-[10px] font-black uppercase tracking-widest opacity-60">Última atualização: {TERMS_DOCUMENT.updatedAt}</p>

            {TERMS_DOCUMENT.sections.map((section) => (
              <section key={section.heading}>
                <h4 className="font-black text-slate-900 dark:text-white uppercase tracking-wider mt-8 border-b dark:border-slate-800 pb-2">{section.heading}</h4>
                <div className="space-y-3 text-sm leading-relaxed mt-3">
                  {section.paragraphs.map((paragraph, idx) => (
                    <p key={idx} className={/^[a-z]\)|^\d+\.\d+\./.test(paragraph) ? 'pl-4' : ''}>{paragraph}</p>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </div>

        <div className="p-5 sm:p-8 border-t dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50 flex flex-col md:flex-row items-center justify-between gap-6">
          <label className="flex items-center gap-3 cursor-pointer group">
            <div 
              onClick={() => setAgreed(!agreed)}
              className={`w-7 h-7 rounded-xl border-2 flex items-center justify-center transition-all ${agreed ? 'bg-indigo-600 border-indigo-600' : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800'}`}
            >
              {agreed && <CheckCircle2 size={18} className="text-white" />}
            </div>
            <span className="text-[11px] font-black text-slate-600 dark:text-slate-400 uppercase tracking-widest group-hover:text-indigo-600 transition-colors">Li e aceito os termos na íntegra</span>
          </label>
          
          <button 
            onClick={onAccept}
            disabled={!agreed}
            className="w-full md:w-auto px-6 sm:px-12 py-4 bg-indigo-600 text-white rounded-2xl font-black uppercase tracking-[0.2em] text-xs shadow-xl shadow-indigo-200 dark:shadow-none hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all active:scale-95"
          >
            Confirmar e Iniciar
          </button>
        </div>
      </div>
    </div>
  );
};

export default TermsOfUse;
